import { OTHERWORLD_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/otherworldBackdrops';
import type { AtmospherePreset } from '../presets.config';
import { ATMOSPHERE_PALETTE as P } from '../palette';
import { breathingGlowBlobs, gustingWeather, multiPlaneParallax, biomeCloser } from '../presetBuilders';
import { BRANCH_DEPTH_BACKDROPS, BRANCH_DEPTH_HEIGHTS } from '../../../game/effects/atmosphere/silhouettes/branchDepthBackdrops';

/** Fungal / Glowspore Grotto — a bioluminescent fungal hollow: surreal MAGENTA + violet +
 *  bio-CYAN + soft pink glows on a near-black backdrop so the light POPS. Twinkling spore motes,
 *  eerie magenta light-shafts, pulsing mushroom-cap glow blobs, drifting luminous rising spores,
 *  a churning bio-energy bed, floor cap-flicker, giant glowing mushroom-cap silhouettes (parallax
 *  caps), soft spore-haze, and a saturated magenta-violet grade. DISTINCT from the crystal-BLUE
 *  cave biome — this is dreamy, pink/magenta, alive. */
export const FUNGAL: AtmospherePreset = {
  id: 'fungal',
  layers: [
    // Near-black grotto backplane (PX) — void-violet zenith → murky magenta hollow, so the
    // bioluminescent glows and cap silhouettes read against a dark, contrasty backdrop.
    { kind: 'skyGradient', from: 0x0a0414, to: 0x2a0a30 },
    // Drifting luminous spore motes — twinkling (sparkle texture) pink / bio-cyan / magenta.
    {
      kind: 'fireflies',
      count: 28, colors: [P.softPink, P.mushroomBlue, P.witherMagenta],
      sizeMin: 5, sizeMax: 15, driftPx: 30,
      periodMinMs: 5000, periodMaxMs: 11000, alphaMin: 0.25, alphaMax: 0.85,
      textureKey: 'sparkle',
    },
    // Eerie bioluminescent light shafts — magenta/violet (cold, surreal, not warm sun).
    {
      kind: 'godRays',
      count: 3, color: P.witherMagenta, widthMin: 50, widthMax: 130, height: 520,
      tiltDeg: 7, periodMinMs: 7000, periodMaxMs: 13000, alphaMin: 0.10, alphaMax: 0.34,
    },
    // Pulsing mushroom-cap glow blobs — bioluminescent caps swelling (slow breathe) — magenta
    // + bio-cyan cap-tops at the parallax cap heights, plus a soft floor bloom.
    // Caps pulse with a slow inner bioluminescence.
    breathingGlowBlobs(
      [
        { color: P.witherMagenta, atX: 0.28, atY: 0.58, size: 360, alpha: 0.14 }, // left cap glow
        { color: P.mushroomBlue, atX: 0.72, atY: 0.5, size: 340, alpha: 0.12 },   // right cap glow
        { color: P.softPink, atX: 0.5, atY: 0.97, size: 560, alpha: 0.12 },       // floor spore-bloom
      ],
      { periodMs: 5400, min: 0.6, max: 1.4 },
    ),
    // Churning bio-energy bed — magenta / violet / bio-cyan drifting lights.
    {
      kind: 'lightField',
      count: 14, palette: [P.witherMagenta, P.corruptViolet, P.mushroomBlue],
      sizeMin: 100, sizeMax: 260, alphaMin: 0.05, alphaMax: 0.18,
      driftRadius: 70, periodMinMs: 6000, periodMaxMs: 12000,
    },
    // Pulsing bioluminescent cells — soft magenta + bio-cyan glow organs swelling
    // + receding on a slow organic cycle (distinct from the breathing glow blobs:
    // these are smaller, scattered cells, not the big cap-tops). Saturated colours
    // + alphaMax 0.6 so they read over the near-black grotto sky.
    {
      kind: 'bioluminescence', color: P.witherMagenta, count: 8,
      sizeMin: 80, sizeMax: 180, alphaMin: 0.2, alphaMax: 0.6,
      bandYMin: 0.3, bandYMax: 0.85, driftPx: 18,
      periodMinMs: 4000, periodMaxMs: 8000,
    },
    // Rising luminous spores — pink drifting up, fading toward bio-cyan; density gusts on a slow cycle.
    gustingWeather(
      { mode: 'rise', color: P.softPink, colorEnd: 0x2a8fa6,
        size: 11, frequencyMs: 120, lifespanMs: 4000, speed: 28, gravityY: -14, windX: 14, alphaEnd: 0.55 },
      { periodMs: 6800, min: 0.5, max: 1.6, phase: 0.2 },
    ),
    // Pulsing cap-glow flicker at the floor — magenta bioluminescence.
    {
      kind: 'flicker', color: P.witherMagenta, size: 260, baseAlpha: 0.09, flickerAmp: 0.07, hzMin: 2, hzMax: 6,
      anchors: [{ atX: 0.2, atY: 0.92 }, { atX: 0.5, atY: 0.95 }, { atX: 0.8, atY: 0.92 }],
    },
    // Giant glowing mushroom-cap silhouettes (PX) read against the murky magenta horizon — far
    // caps (peaks, hazy + swaying) → a single mid cap (cone) → near caps (peaks, dark + opaque).
    // All sit on the scene horizon (y=0 lift); silhouettes stay darker than the sky so they pop,
    // the bioluminescence comes from the glow blobs + spores layered over them.
    ...multiPlaneParallax([
      { svg: BRANCH_DEPTH_BACKDROPS.fungal, shape: 'peaks', color: 0x30143a, speed: 0.04, height: BRANCH_DEPTH_HEIGHTS.fungal, alpha: 0.52,
        detail: 0.8, sway: { ampPx: 12, periodMs: 28000 } },
      { svg: OTHERWORLD_BACKDROPS.fungal.mid, shape: 'cone', color: 0x1c0a26, speed: 0.10, height: 300, alpha: 0.86,
        detail: 1.1 },
      { svg: OTHERWORLD_BACKDROPS.fungal.near, shape: 'peaks', color: 0x12061a, speed: 0.19, height: 190, alpha: 0.95,
        detail: 1.1 },
    ]),
    // Soft drifting spore-haze through the hollow.
    { kind: 'fog', color: P.corruptViolet, bands: 4, alphaMin: 0.04, alphaMax: 0.11, driftPx: 50, periodMinMs: 9000, periodMaxMs: 16000 },
    ...biomeCloser({
      vignette: { color: 0x0a0414, edgeAlpha: 0.38, washColor: 0xd633ff, washAlpha: 0.06 },
      // Saturated magenta-violet grade ('normal' keeps the grotto DARK + vivid, not washed pale).
      grade: { top: 0xc24dff, bottom: 0x1a0826, strength: 0.11, blend: 'normal' },
    }),
  ],
};
